import * as Sentry from '@sentry/browser'
import { push } from 'connected-react-router'
import LogRocket from 'logrocket'
import { call, put, take, fork, select } from 'redux-saga/effects'
import { clearLanguageCode } from 'config/i18n'
import { handleRequest } from './requests'
import {
    ProfileActions,
    saveProfileDetails,
    ResetPasswordRequestPayload,
    UpdatePasswordRequestPayload,
    BasicSignUpResetPayload,
    login as loginAction,
} from '../state/profile'
import { STATE_KEYS } from '../state/requests'
import { saveToken, clearToken } from '../services/auth'
import { api } from '../services/api'
import {
    LoginRequest,
    LoginResponse,
    login,
    LogoutResponse,
    logout,
    resetPassword,
    updatePassword,
    ProfileRequest,
    ProfileResponse,
    getProfile,
    verifyToken,
} from '../services/api/profile'
import { selectTeamFilter, setTeamFilter } from 'state/filter'
import { resetAnalytic } from 'config/analytics'
import { saveAuthErrors, resetAuthErrors } from 'state/error/authErrors'

export function* handleGetProfile(payload?: ProfileRequest): any {
    const teamFilter = yield select(selectTeamFilter)
    yield handleRequest<ProfileRequest, ProfileResponse>({
        stateKey: payload?.stateKey ? payload.stateKey : STATE_KEYS.Profile,
        payload: {},
        ApiHandler: getProfile,
        *onSuccess(response) {
            if (response && response.data) {
                const { data } = response.data
                LogRocket.identify(data.email)
                if (!teamFilter && !data.is_internal_staff && data.teams.length > 0) {
                    yield put(setTeamFilter(data.teams[0].uuid))
                }
                yield put(saveProfileDetails({ data }))
                if (payload?.onSuccess) {
                    yield payload.onSuccess(data)
                }
                Sentry.configureScope((scope: any) => {
                    scope.setUser(data)
                })
            }
        },
    })
}

export function* watchProfileRequestFlow(): any {
    while (true) {
        const action = yield take(ProfileActions.PROFILE_REQUEST)
        yield fork(handleGetProfile, action.payload)
    }
}

export function* handleLoginRequest(payload: LoginRequest): any {
    api.deleteHeader('Authorization')
    yield handleRequest<LoginRequest, LoginResponse>({
        stateKey: STATE_KEYS.Login,
        payload,
        ApiHandler: login,
        *onSuccess(response) {
            const token = response.data ? response.data.data.key : ''
            api.setHeader('Authorization', `Token ${token}`)
            yield call(saveToken, token)
            yield put({ type: ProfileActions.PROFILE_REQUEST })
            const member_uuid = response.data?.data?.member_uuid
            const isRegistered = response.data?.data?.is_registered

            const redirectLink = payload?.redirectLink
                ? payload?.redirectLink
                : member_uuid && !isRegistered
                  ? `/register?member=${member_uuid}`
                  : '/'
            yield put(push(redirectLink))
        },
    })
}

export function* watchLoginRequestFlow(): any {
    while (true) {
        const action = yield take(ProfileActions.LOGIN_REQUEST)
        yield fork(handleLoginRequest, action.payload)
    }
}

export function* handleLogoutRequest(): any {
    yield handleRequest<Record<string, never>, LogoutResponse>({
        stateKey: STATE_KEYS.Logout,
        payload: {},
        ApiHandler: logout,
        *onSuccess(response) {
            clearToken()
            resetAnalytic()
            api.setHeader('Team', '')
            api.setHeader('Authorization', '')
            api.setHeader('Accept-Language', 'en')
            yield put(push('/login'))
            clearLanguageCode()
        },
    })
}

export function* watchLogoutRequestFlow(): any {
    while (true) {
        yield take(ProfileActions.LOGOUT_REQUEST)
        yield fork(handleLogoutRequest)
    }
}

export function* handleResetPasswordRequest(payload: ResetPasswordRequestPayload): any {
    yield handleRequest<ResetPasswordRequestPayload, any>({
        stateKey: STATE_KEYS.ResetPassword,
        payload,
        ApiHandler: resetPassword,
        *onSuccess(response) {
            if (payload?.onSuccess) {
                payload.onSuccess()
            }
        },
        *onError(response) {
            const errors = response?.validation_errors
            if (errors) {
                yield put(resetAuthErrors())
                yield put(saveAuthErrors(errors))
            }
        },
    })
}

export function* watchResetPasswordRequestFlow(): any {
    while (true) {
        const action = yield take(ProfileActions.RESET_PASSWORD_REQUEST)
        yield fork(handleResetPasswordRequest, action.payload)
    }
}

export function* handleUpdatePasswordRequest(payload: UpdatePasswordRequestPayload): any {
    yield handleRequest<UpdatePasswordRequestPayload, any>({
        stateKey: STATE_KEYS.UpdatePassword,
        payload,
        ApiHandler: updatePassword,
        *onSuccess(response) {
            if (payload?.onSuccess) {
                payload.onSuccess()
            }
            if (payload.isSignUp) {
                yield put(
                    loginAction({
                        email: payload.email,
                        password: payload.new_password1,
                        redirectLink: payload?.isSignUp ? '/campaigns' : null,
                    }),
                )
            }
        },
        *onError(response) {
            const errors = response?.validation_errors
            if (errors) {
                yield put(resetAuthErrors())
                yield put(saveAuthErrors(errors))
            }
        },
    })
}

export function* watchUpdatePasswordRequestFlow(): any {
    while (true) {
        const action = yield take(ProfileActions.UPDATE_PASSWORD_REQUEST)
        yield fork(handleUpdatePasswordRequest, action.payload)
    }
}

export function* handleVerifyTokenRequest(payload: BasicSignUpResetPayload): any {
    yield handleRequest<BasicSignUpResetPayload, any>({
        stateKey: STATE_KEYS.VerifyToken,
        payload,
        ApiHandler: verifyToken,
        *onSuccess(response) {
            const { data } = response
            if (data?.data?.email && payload.onSuccess) {
                payload.onSuccess(data?.data?.email)
            }
        },
        *onError(response) {
            const isExpired = response?.validation_errors?.link?.code === 'EXPIRED_LINK'
            if (payload.onError && isExpired) {
                payload.onError()
            }
        },
    })
}

export function* watchVerifyTokenRequestFlow(): any {
    while (true) {
        const action = yield take(ProfileActions.VERIFY_TOKEN_REQUEST)
        yield fork(handleVerifyTokenRequest, action.payload)
    }
}
